<template>
	<div v-if="isUserAllowedInLevel5or6 && isAuthenticatedUserInGroupAdmin">
		<BaseHeader :title="$t('manage_subscription')"></BaseHeader>
		<div v-if="!loading" class="m-8">
			<div class="w-full flex flex-row md:gap-8">
				<!-- Subscription Management or Checkout -->
				<div class="w-full p-8">
					<!-- Check if the user has an active subscription -->
					<div class="flex flex-row">
						<div v-if="stripeCustomer && stripeCustomer.subscriptions && stripeCustomer.subscriptions.length > 0">
							<!-- User has an active subscription -->
							<div
								v-for="subscription in stripeCustomer.subscriptions"
								:key="subscription.id"
								class="p-8 border rounded bg-gray-300 flex flex-col"
								style="text-align: start"
							>
								<div style="text-align: start" class="mb-6 font-bold text-lg">{{ $t('chosen_model') }}</div>
								<div class="flex flex-row gap-16 mb-8">
									<div class="flex flex-col">
										<div>
											<strong>{{ $t('subscription') }}:</strong>
										</div>
										<div>
											<strong>{{ $t('status') }}:</strong>
										</div>
										<div>
											<strong>{{ $t('sub_range') }}:</strong>
										</div>
										<div>
											<strong>{{ $t('next_billing_time') }}:</strong>
										</div>
									</div>
									<div class="flex flex-col">
										<div>
											{{ subscription.name }}
										</div>
										<div>
											{{ getSubscriptionStatus(subscription) }}
										</div>
										<div>
											{{ getGermanDate(subscription.current_period_start) }} -
											{{ getGermanDate(subscription.current_period_end) }}
										</div>
										<div>
											{{ getGermanDate(subscription.current_period_end) }}
										</div>
									</div>
								</div>

								<BaseButton @click="manageSubscription" class="btn-blue mt-4">{{ $t('manage_subscription') }}</BaseButton>
							</div>
						</div>

						<div v-else>
							<!-- User does not have a subscription -->
							<div class="font-bold" style="text-align: start">{{ $t('no_active_sub_found') }}</div>
							<div class="flex gap-12 mt-10 flex-shrink">
								<div v-for="plan in plans" :key="plan.price_id" class="p-8 border rounded bg-gray-300 h-1/3">
									<div class="flex flex-col justify-between gap-12" style="text-align: start">
										<div class="flex flex-col">
											<div class="text-lg font-bold mb-4">{{ plan.product.name }}</div>
											<div>{{ plan.product.description }}</div>
											<div>
												{{ $t('price') }}: {{ formatMoney(plan.amount) }} {{ plan.currency ? plan.currency.toUpperCase() : 'N/A' }} /
												{{ getPriceInterval(plan.interval, plan.interval_count) }}
											</div>
											<div class="h-12" :class="{ invisible: plan.yearly_saved === 0 }">
												{{ $t('yearly_saved') }}:
												{{ formatMoney(plan.yearly_saved) }}
												{{ plan.currency ? plan.currency.toUpperCase() : 'N/A' }}
											</div>
										</div>
										<BaseButton @click="selectPlan(plan.price_id)" class="btn-blue">{{ $t('pick').toUpperCase() }}</BaseButton>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<LoadingSpinner v-else />
	</div>
	<Forbidden v-else />
</template>

<script>
import Validate from '@/mixins/Validate';
import BaseHeader from '@/components/navigation/BaseHeader.vue';
import Authentication from '@/mixins/Authentication';
import Forbidden from '@/views/default/Forbidden.vue';
import PaymentRepository from '@/api/repositories/PaymentRepository';
import { loadStripe } from '@stripe/stripe-js';
import LoadingSpinner from '@/components/navigation/LoadingSpinner.vue';
import BaseButton from '@/components/forms/buttons/BaseButton.vue';
import { getPaymentStatus, getPriceInterval, getSubscriptionStatus } from '@/helpers/Utils';
import { getGermanDate } from '../../../../plugins/Utils';
import Formatter from '@/mixins/Formatter';

export default {
	name: 'ManageSubscription',
	components: { BaseButton, LoadingSpinner, Forbidden, BaseHeader },
	mixins: [Validate, Authentication, Formatter],
	data() {
		return {
			loading: false,
			disableButton: false,
			stripe: null,
			stripeCustomer: null,
			plans: [],
			selectedPlanId: null, // Track the selected plan ID
			selectedProduct: null, // Track the selected product details
		};
	},
	methods: {
		getGermanDate,
		getPaymentStatus,
		getPriceInterval,
		getSubscriptionStatus,
		async initializeStripe() {
			try {
				const stripePublicKey = process.env.VUE_APP_STRIPE_PK;
				if (!stripePublicKey) {
					throw new Error('Stripe public key is missing');
				}
				this.stripe = await loadStripe(stripePublicKey);
			} catch (error) {
				console.error('Stripe initialization error:', error);
			}
		},
		async fetchStripeCustomer() {
			try {
				const response = await PaymentRepository.getMe();
				this.stripeCustomer = response.data.data;
			} catch (error) {
				console.error(error);
			}
		},
		async fetchPlans() {
			try {
				const response = await PaymentRepository.getSalesProducts();
				this.plans = response.data.data;
			} catch (error) {
				console.error(error);
			}
		},
		async selectPlan(priceId) {
			this.selectedPlanId = priceId;
			try {
				const response = await PaymentRepository.checkout({ price_id: this.selectedPlanId });
				const { session_id } = response.data;

				// Redirect the user to the Stripe Checkout page
				const { error } = await this.stripe.redirectToCheckout({ sessionId: session_id });
				if (error) {
					console.error('Stripe Checkout redirect error:', error);
				}
			} catch (error) {
				console.error('Error during subscription checkout:', error);
			}
		},
		async manageSubscription() {
			try {
				const response = await PaymentRepository.portal();
				const { url } = response.data;

				// Redirect the user to the Stripe Billing Portal
				window.location.href = url;
			} catch (error) {
				console.error('Error accessing the billing portal:', error);
			}
		},
		formatDate(timestamp) {
			const date = new Date(timestamp);
			return date.toLocaleDateString();
		},
	},
	async mounted() {
		if (this.isUserAllowedInLevel5or6 && this.isAuthenticatedUserInGroupAdmin) {
			this.loading = true;
			this.disableButton = true;
			await this.initializeStripe();
			await this.fetchStripeCustomer();
			await this.fetchPlans();

			if (this.stripeCustomer && this.stripeCustomer.subscriptions && this.stripeCustomer.subscriptions.length < 0) {
				this.$swal(this.$t('general.attention').toString(), this.$t('no_active_sub_found').toString(), 'warning');
			}

			this.loading = false;
			this.disableButton = false;
		}
	},
};
</script>
